import { z } from 'zod'

const position = z.object({ x: z.number(), y: z.number(), z: z.number() })
const normal = position.refine(({ x, y, z }) => x !== 0 || y !== 0 || z !== 0)
const hit = z.object({ position, normal })
const path = z
  .object({
    points: z.array(position).min(2),
    vertexNormals: z.array(normal),
    closed: z.boolean(),
  })
  .refine(({ points, vertexNormals }) => points.length === vertexNormals.length)

const projection = z.object({
  modelId: z.string(),
  sourceKey: z.string(),
  // Older saved projections lack a context key and must be recomputed once.
  contextKey: z.string().optional(),
  paths: z.array(path),
  center: hit.nullish(),
  diagonal: z.number().nonnegative(),
  complete: z.boolean(),
})

export type KVPhotoProjectionHit = z.infer<typeof hit>
/** Photo projections use the same Unreal world coordinates as polygon markup. */
export type KVPhotoAnnotationProjection = z.infer<typeof projection>

export function isPhotoAnnotationProjection(
  value: unknown
): value is KVPhotoAnnotationProjection {
  return projection.safeParse(value).success
}

/** Calibrated photo annotation projections, keyed by annotation id. */
export type KVPhotoAnnotationProjections = Record<
  string,
  KVPhotoAnnotationProjection
>

/** Keeps only the valid projections from a stored projection map. */
export function parsePhotoAnnotationProjections(
  value: unknown
): KVPhotoAnnotationProjections {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value).filter(([, projection]) =>
      isPhotoAnnotationProjection(projection)
    )
  )
}
